<script>
  import Page from '@components/page'
  import Block from '@components/general/block'
  import Table from '@components/table'
  import Button from '@components/buttons/button.svelte'
  import BoxedText from '@components/general/boxed-text.svelte'
  import ContributorLink from '@components/contributors/contributor-link.svelte'
  import { useForm } from '@inertiajs/svelte'
  import { format, differenceInMinutes } from 'date-fns'
  import { formatDate, formatTime, formatDateTime } from '@lib/utils/date'
  import { colors } from '@lib/utils/colors'
  import { cn } from '@lib/utils'
  import { Video, Pencil, RefreshCw, Globe, Users } from 'lucide-svelte'
  import Tag from '@components/general/tag.svelte'

  let { live_session } = $props()

  let title = $derived(live_session?.event_name || "Live Session")
  let color = $derived(live_session?.color || 'gray')

  let duration = $derived.by(() => {
    const mins = differenceInMinutes(new Date(live_session.end_time), new Date(live_session.start_time))
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60)
      const rem = mins % 60
      return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`
    }
    return `${mins}m`
  })

  let isPast = $derived(new Date(live_session.end_time) < new Date())

  const sync = useForm()

  function syncSession() {
    $sync.post(`/admin/live_sessions/${live_session.id}/sync`)
  }
</script>

<Page.Layout {title}>

  <Page.Navigation {title} Icon={Video} links={[
    { text: "Admin", href: "/admin" },
    { text: "Live Sessions", href: "/admin/live_sessions" }
  ]} />

  <Page.Titles {title} subtitle={live_session.arlo_code}>
    {#snippet buttons()}
      <Button path={`/live-sessions/${live_session.id}`} Icon={Globe} hint="View live session" />
      <Button text="Sync with Arlo" onclick={syncSession} Icon={RefreshCw} disabled={$sync.processing} />
      <Button text="Edit" path={`/admin/live_sessions/${live_session.id}/edit`} Icon={Pencil} />
    {/snippet}
  </Page.Titles>

  <Page.Content>

    {#snippet main()}

      <!-- Session details -->
      <Block.Element label="Session details" title={live_session.event_name}>
        <Block.Items>
          <Block.Item label="Date" value={format(live_session.start_time, 'EEE, d MMMM yyyy')} />
          <Block.Item label="Time" value={`${formatTime(live_session.start_time, { mode: '12h' })} – ${formatTime(live_session.end_time, { mode: '12h' })}`} />
          <Block.Item label="Duration" value={duration} />
          {#if live_session.arlo_code}
            <Block.Item label="Session code" value={live_session.arlo_code} />
          {/if}
          <Block.Item label="Status">
            <BoxedText variant={isPast ? "default" : "success"} size="xs" text={isPast ? "Past" : "Upcoming"} />
          </Block.Item>
          <Block.Item label="Color">
            <div class="flex items-center gap-2">
              <div class={cn(colors[color]?.base, "w-3 h-3 rounded-full")}></div>
              <span class="text-sm text-gray-600 capitalize">{color}</span>
            </div>
          </Block.Item>
          <Block.Item label="Presenter">
            <ContributorLink contributor={live_session.contributor} admin />
          </Block.Item>
          {#if live_session.tags?.length > 0}
            <Block.Item label="Topics">
              <div class="flex flex-wrap gap-1">
                {#each live_session.tags as tag (tag.id)}
                  <Tag {tag} size="xs" variant="faint" />
                {/each}
              </div>
            </Block.Item>
          {/if}
        </Block.Items>

        {#if live_session.description?.html}
          <div class="prose prose-sm mt-4 pt-4 border-t border-gray-100">
            {@html live_session.description.html}
          </div>
        {/if}
      </Block.Element>

    {/snippet}

    {#snippet aside()}

      <!-- Stats -->
      <Block.Element label="Registration stats" title="Registrations">
        <Block.Items>
          <Block.Item label="Total registrations" value={live_session.registrations_count} />
        </Block.Items>
      </Block.Element>

    {/snippet}

  </Page.Content>

  <!-- Registrations table -->
  <Page.Content>
    <div class="flex justify-between items-center mb-2">
      <div class="flex items-center gap-2">
        <Users size={16} strokeWidth={1.5} class="text-gray-500" />
        <span class="text-sm font-medium text-gray-700">Registrations</span>
      </div>
    </div>

    <Table.Root empty={!live_session.registrations?.length} emptyMessage="No registrations yet">
      <Table.Head>
        <Table.Header width="45%">Member</Table.Header>
        <Table.Header width="35%">Email</Table.Header>
        <Table.Header width="20%">Registered</Table.Header>
      </Table.Head>
      <Table.Body>
        {#each live_session.registrations as reg (reg.id)}
          <Table.Row>
            <Table.Cell width="45%">{reg.user_name}</Table.Cell>
            <Table.Cell width="35%">{reg.user_email}</Table.Cell>
            <Table.Cell width="20%">
              <span class="text-xs text-gray-500">{formatDateTime(reg.registered_at)}</span>
            </Table.Cell>
          </Table.Row>
        {/each}
      </Table.Body>
    </Table.Root>
  </Page.Content>

</Page.Layout>
